import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Building2, CheckCircle, Clock, AlertCircle, FileText, CheckSquare, Activity, LayoutDashboard } from 'lucide-react';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [cycle, setCycle] = useState(null);
  const [daysUntilDeadline, setDaysUntilDeadline] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/admin/dashboard');
      if (response.data.success) {
        const data = response.data.data;
        setStats(data.stats);
        setCycle(data.cycle);
        setDaysUntilDeadline(data.daysUntilDeadline);
        setRecentActivity(data.recentActivity);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const StatCard = ({ title, value, color, icon: Icon }) => (
    <div className={`${color} rounded-xl shadow-sm p-6 transform hover:scale-105 transition-transform duration-200`}>
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-sm font-medium text-white opacity-90">{title}</h3>
          <p className="text-3xl font-bold text-white mt-2">{value}</p>
        </div>
        {Icon && <Icon className="text-white opacity-80 w-8 h-8" />}
      </div>
    </div>
  );

  const pieData = [
    { name: 'Approved', value: parseInt(stats?.approved || 0), color: '#10B981' },
    { name: 'Under Review', value: parseInt(stats?.under_review || 0), color: '#8B5CF6' },
    { name: 'Submitted', value: parseInt(stats?.submitted || 0), color: '#3B82F6' },
    { name: 'Not Started/Draft', value: parseInt(stats?.not_started || 0), color: '#6B7280' },
  ].filter(d => d.value > 0);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
            <LayoutDashboard className="text-primary w-6 h-6" />
            <span>Admin Dashboard</span>
          </h1>
          <p className="text-sm text-gray-500 mt-1">Overview of broker compliance</p>
        </div>
        {cycle && (
          <div className="text-right">
            <p className="text-sm text-gray-500">Current Cycle</p>
            <p className="font-semibold text-gray-900">{cycle.title}</p>
            {daysUntilDeadline !== null && (
              <p className={`text-sm font-medium mt-1 ${daysUntilDeadline < 0 ? 'text-red-600 bg-red-50 inline-block px-2 py-0.5 rounded-full' : daysUntilDeadline < 7 ? 'text-orange-600 bg-orange-50 inline-block px-2 py-0.5 rounded-full' : 'text-primary bg-green-50 inline-block px-2 py-0.5 rounded-full'}`}>
                {daysUntilDeadline < 0 ? `${Math.abs(daysUntilDeadline)} days overdue` : `${daysUntilDeadline} days until deadline`}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <StatCard title="Total Firms" value={stats?.total_firms || 0} color="bg-gradient-to-br from-primary to-green-600" icon={Building2} />
        <StatCard title="Submitted" value={stats?.submitted || 0} color="bg-gradient-to-br from-blue-500 to-blue-600" icon={FileText} />
        <StatCard title="Under Review" value={stats?.under_review || 0} color="bg-gradient-to-br from-purple-500 to-purple-600" icon={Clock} />
        <StatCard title="Approved" value={stats?.approved || 0} color="bg-gradient-to-br from-green-500 to-green-600" icon={CheckCircle} />
        <StatCard title="Not Started" value={stats?.not_started || 0} color="bg-gradient-to-br from-gray-500 to-gray-600" icon={AlertCircle} />
        <StatCard title="Avg Completion" value={`${Math.round(stats?.avg_completion || 0)}%`} color="bg-gradient-to-br from-orange-400 to-orange-500" icon={CheckSquare} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Charts */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
            <Activity className="w-5 h-5 text-gray-500" />
            <span>Submission Status Distribution</span>
          </h2>
          <div className="h-64">
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => [`${value} Firms`, 'Count']}
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                  />
                  <Legend verticalAlign="bottom" height={36} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-400">
                No submission data available
              </div>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
            <Activity className="w-5 h-5 text-gray-500" />
            <span>Recent Activity</span>
          </h2>
          {recentActivity.length === 0 ? (
            <p className="text-gray-500">No recent activity</p>
          ) : (
            <div className="space-y-4 max-h-64 overflow-y-auto pr-2">
              {recentActivity.map((activity) => (
                <div key={activity.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                      <span className="text-white text-xs font-medium">
                        {(activity.first_name?.[0] || '') + (activity.last_name?.[0] || '')}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900">
                      <span className="font-medium">
                        {activity.first_name} {activity.last_name}
                      </span>
                      {' '}{activity.action}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(activity.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link
          to="/admin/firms"
          className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow"
        >
          <h3 className="font-semibold text-dark mb-2">Manage Firms</h3>
          <p className="text-sm text-grey">Create and manage broker firms</p>
        </Link>
        <Link
          to="/admin/users"
          className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow"
        >
          <h3 className="font-semibold text-dark mb-2">Manage Users</h3>
          <p className="text-sm text-grey">Create and manage user accounts</p>
        </Link>
        <Link
          to="/admin/submissions"
          className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow"
        >
          <h3 className="font-semibold text-dark mb-2">View Submissions</h3>
          <p className="text-sm text-grey">Review all broker submissions</p>
        </Link>
      </div>
    </div>
  );
};

export default Dashboard;
